import {
	Group,
	EditTenantRequest,
	EditGroupRequest,
	GroupDetails
} from 'Services/Api/Groups/Types';
import { EditGroupValues } from '../Group.types';

/**
 * Whether the Property flag may be toggled from the client's perspective.
 * Nested property → property is supported: an ancestor may already be a property.
 * (Server-side `editGroup` must allow the same; see API validation for `isProperty`.)
 */
export const getCanBePropertyToAncestors = (_group: Group) => true;

export function createInitialValuesDerivedFromParent(
	group: GroupDetails
): Pick<EditGroupValues, 'tenant' | 'parent' | 'canBePropertyToAncestors'> {
	const { name, id, isTenant, moduleSettings, isProperty } = group;
	const canBePropertyToAncestors = getCanBePropertyToAncestors(group);
	const entity = { name, id, moduleSettings, isProperty };
	if (isTenant) {
		return {
			tenant: entity,
			parent: entity,
			canBePropertyToAncestors
		};
	}

	return {
		tenant: group.parents[0],
		parent: entity,
		canBePropertyToAncestors
	};
}

/**
 * Determines the fallback hierarchy label for an existing group when no
 * saved label exists in the hierarchy label backend.
 *
 * Rules:
 * - Level 1 (tenant): always "Organization"
 * - Level 2+ old tenants (is_new=false):
 *   - isProperty=true  → default "Property"
 *   - isProperty=false → no default (user must select)
 * - Level 2+ new tenants (is_new=true): no default (user must pick)
 */
function getEditFallbackHierarchyLabel(group: GroupDetails): string | undefined {
	// Level 1 (tenant) → always Organization
	if (group.isTenant) {
		return 'Organization';
	}

	const isNewTenant = group.is_new;

	// New tenants: level 2+ has no default — user must select
	if (isNewTenant) {
		return undefined;
	}

	// Old tenants (is_new=false or undefined):
	// Level 1 is Organization (handled above for isTenant)
	// Level 2+:
	//   isProperty=true  → "Property"
	//   isProperty=false → no default (user must select)
	if (group.isProperty) {
		return 'Property';
	}

	return undefined;
}

export function createInitialValues(group: GroupDetails): EditGroupValues {
	return {
		id: group.id,
		name: group.name,
		active: group.active,
		syncCode: group.syncCode === null ? null : group.syncCode.trim(),
		isProperty: group.isProperty,
		moduleEnabled: group.moduleSettings?.moduleEnabled ?? false,
		viewAllCourses: group.moduleSettings?.viewAllCourses ?? false,
		tenantId: group.tenantId,
		parentId: group.parentId,
		tenant: group.parents?.[0],
		parent: group.parents.slice(-1)[0] || null,
		isArchived: group.isArchived,
		canBeProperty: group.canBeProperty,
		canBePropertyToAncestors: getCanBePropertyToAncestors(group),
		systemId: group.systemId,
		autoactivate: group.autoactivate,
		sso: group.sso,
		domains: group.domains,
		hierarchyLabel: getEditFallbackHierarchyLabel(group)
	};
}

export function getTenantSubmitValues(
	values: EditGroupValues
): EditTenantRequest['input'] {
	return {
		id: values.id,
		name: values.name,
		tenantId: values.tenantId,
		active: values.active,
		syncCode: values.syncCode || null,
		isProperty: values.isProperty,
		isArchived: values.isArchived,
		moduleEnabled: values.moduleEnabled,
		viewAllCourses: values.viewAllCourses,
		autoactivate: values.autoactivate,
		sso: values.autoactivate ? values.sso : false,
		domains: values.domains
	};
}

export function getGroupSubmitValues(
	values: EditGroupValues
): EditGroupRequest['input'] {
	return {
		id: values.id,
		parentId: values.parentId,
		name: values.name,
		active: values.active,
		syncCode: values.syncCode || null,
		isProperty: values.isProperty,
		isArchived: values.isArchived,
		moduleEnabled: values.moduleEnabled,
		autoactivate: values.autoactivate,
		sso: false,
		domains: null
	};
}
